<template>
  <BaseDetails class="base-details">
    <template v-slot:header>
      <h2 class="pb-2 pt-2">Basic Details</h2>
      <Permissioned section="bookings" :id="bookingId">
        <PrimaryButton
          permission="edit"
          @click.native="openBasicDetailsEdit"
          light
          dense
          shrink
          class="edit-button"
        >
          Edit
          <template v-slot:append:icon><v-icon>mdi-pencil</v-icon></template>
        </PrimaryButton>
      </Permissioned>
    </template>

    <template v-slot:body>
      <EditBasicDetails
        :booking="booking"
        v-if="isEditOpen"
        :is-open="isEditOpen"
        @close="closeBasicDetailsEdit"
      />
      <Permissioned section="bookings" vertical :id="bookingId">
        <LabeledText label="Job Title" permission="view.role-title">
          {{ booking.title }}
        </LabeledText>
        <LabeledText permission="view.grade" label="Grade">
          {{ booking.grade || "-" }}
        </LabeledText>
        <LabeledText label="Salary" v-if="showSalary">
          {{ annualSalary }}
        </LabeledText>
        <LabeledText label="Contract Type & Working Hours">
          <v-chip permission="view.contract-type" label>
            {{ booking.contractType || "-" }}
          </v-chip>
          <v-chip permission="view.working-hours" label>
            {{ booking.workingHours || "-" }}
          </v-chip>
        </LabeledText>
        <LabeledText label="Function">
          {{ experienceFunctionName }}
        </LabeledText>
        <LabeledText label="Location" permission="view.location">
          {{ booking.location.line1 }}, {{ booking.location.line2 }},
          {{ booking.location.line3 }}
        </LabeledText>
      </Permissioned>
      <Permissioned
        section="bookings"
        class="d-flex flex-column"
        :id="bookingId"
      >
        <div>
          <Permissioned
            section="bookings"
            :id="bookingId"
            class="d-flex flex-column"
          >
            <LabeledText label="Closing Date" permission="view.deadline-date">
              {{ formattedDeadlineDate }}
            </LabeledText>
            <LabeledText
              label="Proposed Start Date"
              permission="view.start-date"
            >
              {{ formattedStartDate }}
            </LabeledText>
          </Permissioned>
        </div>
        <div class="pt-1">
          <h2>Description</h2>
          <DescriptionTabs
            isHTML
            :descriptions="descriptions"
            class="full-width"
          />
        </div>
        <div class="mt-4" v-if="hasAttachments" perrmision="view.file">
          <FileComponent
            class="pb-2"
            v-for="file in booking.files"
            :key="file.id"
            :file="file"
            @show="showFile(file)"
            customActions
            disabled
            :showFileName="!isMobile"
          />
        </div>
        <PDFViewFull :data="fileDialog" />
      </Permissioned>
      <Permissioned section="bookings" :id="bookingId">
        <LabeledText
          label="Total Cost"
          subLabel="(at Standard Rate)"
          permission="view.total-cost"
        >
          {{ totalCost | currency(currencyCode) }}
        </LabeledText>
      </Permissioned>
      <v-divider />
    </template>
  </BaseDetails>
</template>

<script>
import LabeledText from "@/components/common/BaseDetails/LabeledText";
import BaseDetails from "@/components/common/BaseDetails/BaseDetails";
import PrimaryButton from "@/components/common/Button/PrimaryButton";
import EditBasicDetails from "@/views/bookings/EditBooking/EditBasicDetails";
import { GET_CURRENT_BOOKING } from "@/store/modules/bookings/getters";
import { createNamespacedHelpers } from "vuex";
import { BOOKINGS_NAMESPACE } from "@/store/modules/bookings/actions";
import Permissioned from "@/components/common/Permissioned";
import DescriptionTabs from "@/views/bookings/components/DescriptionTabs";
import { isPermissioned } from "@/utils/permissions";
import { DEFAULT_CURRENCY } from "@/constants/common";
import moment from "moment";
import { getToken } from "@/services/utils";
import FileComponent from "@/components/common/FileComponent";
import PDFViewFull from "@/components/common/PDFViewFull";
import { size } from "lodash";

const { mapGetters } = createNamespacedHelpers(BOOKINGS_NAMESPACE);

export default {
  name: "BasicDetails",
  components: {
    Permissioned,
    EditBasicDetails,
    PrimaryButton,
    LabeledText,
    BaseDetails,
    DescriptionTabs,
    FileComponent,
    PDFViewFull
  },
  data() {
    return {
      isEditOpen: false,
      fileDialog: { dialog: false }
    };
  },
  computed: {
    ...mapGetters({
      currentBooking: GET_CURRENT_BOOKING
    }),
    descriptions() {
      return [
        isPermissioned(
          `bookings[${this.bookingId}].view.internal-description`
        ) && {
          label: "Internal",
          value: this.booking.internalDescription
        },
        isPermissioned(
          `bookings[${this.bookingId}].view.external-description`
        ) &&
          this.booking.externalDescription && {
            label: "External",
            value: this.booking.externalDescription
          }
      ];
    },
    booking() {
      return this.currentBooking;
    },
    bookingId() {
      return this.booking.id;
    },
    totalCost() {
      return this.currentBooking.totalCost ? this.currentBooking.totalCost : 0;
    },
    experienceFunctionName() {
      return this.booking.function ? this.booking.function.name : "-";
    },
    currencyCode() {
      return this.currentBooking.currencyCode || DEFAULT_CURRENCY;
    },
    salaryCurrencyCode() {
      return this.booking.salaryCurrencyCode || DEFAULT_CURRENCY;
    },
    showSalary() {
      return (
        isPermissioned(
          `bookings[${this.currentBooking.id}].view.salary-band-upper`
        ) ||
        isPermissioned(
          `bookings[${this.currentBooking.id}].view.salary-band-lower`
        )
      );
    },
    showLowerAndUpperSalary() {
      return (
        isPermissioned(
          `bookings[${this.currentBooking.id}].view.salary-band-upper`
        ) &&
        isPermissioned(
          `bookings[${this.currentBooking.id}].view.salary-band-lower`
        )
      );
    },
    salaryBandLower() {
      const currency = this.$options.filters.currency;
      return (
        isPermissioned(
          `bookings[${this.currentBooking.id}].view.salary-band-lower`
        ) &&
        `${currency(this.booking.salaryBandLower, this.salaryCurrencyCode)}`
      );
    },
    salaryBandUpper() {
      const currency = this.$options.filters.currency;
      return (
        isPermissioned(
          `bookings[${this.currentBooking.id}].view.salary-band-upper`
        ) &&
        `${currency(this.booking.salaryBandUpper, this.salaryCurrencyCode)}`
      );
    },
    annualSalary() {
      return this.showLowerAndUpperSalary
        ? `${this.salaryBandLower}-${this.salaryBandUpper}`
        : this.salaryBandLower || this.salaryBandUpper;
    },
    formattedDeadlineDate() {
      return moment(this.booking.deadlineDate).format("dddd Do MMMM YYYY");
    },
    formattedStartDate() {
      return moment(this.booking.startDate).format("dddd Do MMMM YYYY");
    },
    hasAttachments() {
      return size(this.booking.files);
    }
  },
  methods: {
    openBasicDetailsEdit() {
      this.isEditOpen = true;
    },
    closeBasicDetailsEdit() {
      this.isEditOpen = false;
    },
    showFile(attachment) {
      this.fileDialog = {
        src: this.fileUrl(attachment.url),
        title: attachment.fileName,
        dialog: true
      };
    },
    fileUrl(url) {
      const token = `token=${getToken()}`;
      return `${url}?${token}`;
    }
  }
};
</script>

<style lang="scss">
.base-details {
  .v-chip {
    margin: 0.5rem 0.5rem 0 0;
    font-weight: bold;
  }

  .labeled-text .content {
    position: relative;
  }

  .sublabel {
    position: absolute;
    top: 0;
    left: 95px;
    width: 300px;
  }
}
</style>
